import React, { FC, useRef, useEffect, useState, ReactElement } from 'react'
import _ from 'lodash'

import styled from "styled-components"
import { Colors } from 'styles/app-styles'
import toggles from 'feature-toggles'

const TIP_PADDING = 5;
const ARROW_WIDTH = 5;

type Position = 'top' | 'left' | 'right' | 'bottom' | 'bottom left'
type Element = ReactElement | string
type WithTipProps = {
  position: Position
  children: [Element, Element]
}

export const WithTip: FC<WithTipProps> = (props) => {
  const [show, setShow] = useState<boolean>(false)
  const [targetBox, setTargetBox] = useState<DOMRect|null>(null)
  const $target = useRef(null)

  useEffect(() => {
    const box = _.invoke($target, 'current.getBoundingClientRect')
    if (box && !isEqual(targetBox, box)) {
      setTargetBox(box)
    }
  })

  const [tip, target] = props.children

  const el = <Tip show={show} target={targetBox} position={props.position}>{tip}</Tip>
  
  useEffect(() => {
    if (!toggles.globalToolTips()) return
    const setTip = setGlobalTip
    if (setTip) setTip(show ? el : <></>)
  }, [show, el])

  return <>
    { !toggles.globalToolTips() && el }
    <div
      ref={$target}
      style={{zIndex:100}}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      >
        {target}
      </div>
    </>
}

type NullableDOMRect = DOMRect | null
type TipProps = {
  children: Element
  position: Position
  show: boolean
  target: NullableDOMRect
}

const Tip: FC<TipProps> = (props) => {
  const [tipBox, setTipBox] = useState(null)
  const $tip = useRef(null)
  const tip = props.children
  useEffect(() => {
    const box = _.invoke($tip, 'current.getBoundingClientRect')
    if (box && !isEqual(tipBox, box)) {
      setTipBox(box)
    }
  })
  return (
    <TipBody position={props.position} show={props.show} target={props.target} tip={tipBox}>
      <Arrow tip={tipBox} position={props.position}/>
      <div ref={$tip}>{tip}</div>
    </TipBody>)
}

type TipBodyProps = {
  position: Position
  show: boolean
  target: NullableDOMRect
  tip: NullableDOMRect
}

const TipBody = styled.div<TipBodyProps>`
  ${props => {
    if(!props.target || !props.tip) return
    switch(props.position) {
      case 'bottom left': return `
        top: ${props.target.bottom}px;
        left: ${props.target.left}px;`
      case 'bottom': return `
        top: ${props.target.bottom}px;
        left: ${(props.target.width - (props.tip.width + 2*TIP_PADDING))/2 + props.target.left}px;`
      case 'top': return `
        left: ${(props.target.width - (props.tip.width + 2*TIP_PADDING))/2 + props.target.left}px;
        top: ${-(props.tip.height + TIP_PADDING + ARROW_WIDTH)}px;`
      case 'right': return `
        left: ${props.target.right + 0*props.tip.width + TIP_PADDING + ARROW_WIDTH}px;
        top: ${props.target.top - props.tip.height/2}px;`
      case 'left': return `
        left: ${props.target.left - (props.tip.width + 2*TIP_PADDING + ARROW_WIDTH)}px;
        top: 0;`
    }
  }}
  background: ${Colors.black};
  border-radius: 4px;
  box-sizing: border-box;
  color: ${Colors.white};
  font-size: 12px;
  margin: 10px 0;
  padding: ${TIP_PADDING}px 8px;
  opacity: ${props => props.show ? 1 : 0};
  position: fixed;
  transition: visibility 0s;
  transition-delay: 1s;
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  z-index: ${props => props.show ? 999 : 0};

  // ${() => toggles.globalToolTips() ? 'box-shadow: 0 0 3px white;' : ''}
`

type ArrowProps = {
  position: Position
  tip: NullableDOMRect
}

const Arrow = styled.div<ArrowProps>`
  ${props => {
    if (!props.tip) return
    switch(props.position) {
      case 'bottom left': return `
        left: ${TIP_PADDING}px;
        top: -${ARROW_WIDTH}px;`
      case 'bottom': return `
        left: ${(props.tip.width + TIP_PADDING - ARROW_WIDTH)/2}px;
        top: -${ARROW_WIDTH}px;`
      case 'top': return `
        left: ${(props.tip.width + TIP_PADDING - ARROW_WIDTH)/2}px;
        top: ${props.tip.height + 2*TIP_PADDING}px;`
      case 'right': return `
        left: -${ARROW_WIDTH + TIP_PADDING/2}px;
        top: ${(props.tip.height + 2*TIP_PADDING)/2 - ARROW_WIDTH/2}px;`
      case 'left': return `
        left: ${props.tip.width + 2*TIP_PADDING - ARROW_WIDTH/2 - 2}px;  // ! magic value -2
        top: ${(props.tip.height + 2*TIP_PADDING)/2 - ARROW_WIDTH/2}px;`
    }
  }}
  border-left: ${ARROW_WIDTH / 0.866}px solid transparent;
  border-right: ${ARROW_WIDTH / 0.866}px solid transparent;
  border-bottom: ${ARROW_WIDTH}px solid ${Colors.black};
  height: 0;
  position: absolute;
  transform: rotate(${props => ({
    bottom: 0,
    left: 90,
    right: -90,
    top: 180
  }[props.position])}deg);
  // width: 0;
`

// lodash can't compare DOMRect with _.isEqual
function isEqual(box1, box2) {
  if (box1 === box2) return true
  if (!box1 || !box2) return false
  return _.every(['x','y','top','left','right','bottom','width','height'], (k) => box1[k] === box2[k])
}

let setGlobalTip:(Element) => void

export const TooltipHost = () => {
  const [Tip, setTip] = useState(<></>)
  useEffect(() => {
    setGlobalTip = setTip
  }, [])
  return Tip
}
